<template>
  <section :class="`ugc-tray-wrapper zoom-level-${zoomLevel}`">
    <div v-show="$store.state.utils.trayDisabled.swatches" class="disable-swatches"></div>
    <div class="row">
      <div
        :class="[
          'ugc-tray-wrapper-content medium-8 large-6 large-offset-3',
          { columns: !enableSwiper },
        ]"
      >
        <div
          v-if="enableSwiper"
          class="ugc-tray-wrapper-swiper-wrapper"
          key="ugc-buttons-swiper"
        >
          <swiper ref="swiper" :options="swiperOptions">
            <swiper-slide
              v-for="(featureName, index) in ugcButtonsOrder"
              :key="index"
            >
              <!-- a UGC image upload button should always exist because other components fire events even when it is not visible -->
              <ugc-image-upload
                v-if="featureName === 'add-your-image' && hasUgc"
                class="text-center"
                v-show="false"
                @click="setDataLayer"
                :disableButton="!ugc_allowed"
              ></ugc-image-upload>
              <component
                :is="getComponent(featureName)"
                v-bind="getAttributes(featureName)"
                v-on="getEvents(featureName)"
              ></component>
            </swiper-slide>
          </swiper>
        </div>
        <div v-else class="row" key="ugc-buttons-no-swiper">
          <div
            v-for="(featureName, index) in ugcButtonsOrder"
            :class="[
              'columns ugc-tray-wrapper-column small-8',
              buttonCount < 3 ? 'medium-4 large-3' : 'medium-2',
              {
                'small-centered': buttonCount === 1,
                'ugc-tray-wrapper-column--first':
                  index === 0 && buttonCount > 1,
                'ugc-tray-wrapper-column--last':
                  index === buttonCount - 1 && buttonCount > 1,
              },
            ]"
            :key="index"
          >
            <!-- a UGC image upload button should always exist because other components fire events even when it is not visible -->
            <ugc-image-upload
              ref="hiddenImageUpload"
              v-if="featureName === 'add-your-image' && hasUgc"
              class="text-center"
              v-show="false"
              @click="setDataLayer"
              :disableButton="!ugc_allowed"
            ></ugc-image-upload>
            <component
              :key="ugcLastUpdated"
              :is="getComponent(featureName)"
              v-bind="getAttributes(featureName)"
              v-on="getEvents(featureName)"
            ></component>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import customPatternEdit from "@/components/UGC/Tray/CustomPatternEdit";
import customPatternUpload from "@/components/UGC/Tray/CustomPatternUpload";
import ugcImageEdit from "@/components/UGC/Tray/ImageEdit";
import ugcImageUpload from "@/components/UGC/Tray/ImageUpload";
import customCheckerboardCreate from "@/components/UGC/Tray/CustomCheckerboardCreate";
import customCheckerboardEdit from "@/components/UGC/Tray/CustomCheckerboardEdit";
import { Swiper, SwiperSlide } from "vue-awesome-swiper";
import { getUGCDataByID, lastUpdated } from "@/libs/graphics/UGCData";
import AddYourImageGallerySelect from "@/components/molecule.trayNavigationMenu.AddYourImageGallery.vue";
import AddYourImageGalleryEdit from '@/components/molecule.trayNavigationMenu.editImageGallery.vue';

export default {
  name: "UgcTrayWrapper",
  components: {
    customPatternEdit,
    customPatternUpload,
    ugcImageEdit,
    ugcImageUpload,
    customCheckerboardCreate,
    customCheckerboardEdit,
    Swiper,
    SwiperSlide,
    AddYourImageGallerySelect,
    AddYourImageGalleryEdit
  },
  props: {
    forceTermsParam: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      ugc_allowed: true,
      image_library_allowed: true,
      checkerboard_allowed: true,
      enableSwiper: false,
      swiperOptions: {
        slidesPerView: 1.5,
        slidesPerGroup: 1,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
        centerInsufficientSlides: true,
      },
    };
  },
  computed: {
    /**
     * This returns the timestamp of the last time the UGC data was updated.
     * We use this as the key of the ImageEdit component to force a rerender any time the UGC image changes
     */
    ugcLastUpdated() {
      return lastUpdated.timestamp || Date.now();
    },
    nextComponent() {
      return this.$store.getters["ugc/getUGCComponentNotActive"];
    },
    currentPattern() {
      return this.$store.state.patternMaker.currentPattern;
    },
    activeUGCComponent() {
      return this.$store.state.standardNavigation.activeComponent;
    },
    componentHasPattern() {
      return this.$store.state.recipe.recipe_v2.components[this.activeUGCComponent]?.subgroup === "checkerboard-creator";
    },
    hasPattern() {
      return this.currentPattern[this.activeUGCComponent]?.ugc || this.componentHasPattern;
    },
    isApparel() {
      return this.$store.getters["utils/isApparelEnabled"];
    },
    hasUgc() {
      let activeComponent = this.$store.state.standardNavigation.activeComponent;
      const ugcFeatureType = this.$recipe.getUGCFeatureType(activeComponent);
      const isComponentUGC = ugcFeatureType === 'ugc';
      const hasUgcInRecipe = this.$recipe.checkForUGCComponent(activeComponent);
      if (this.isApparel) {
        const activeSubComponent =
          this.$store.state.standardNavigation.activeSubComponent || null;
        if (activeSubComponent) {
          activeComponent = this.$store.state.standardNavigation.activeComponent;
          return (
            !!this.ugcLastUpdated && // this is needed since the UGCData is not reactive and only this property was created to let Vue react to changes in that store
            !!getUGCDataByID(activeComponent, activeSubComponent)
          );
        }
      }
      return (isComponentUGC && hasUgcInRecipe);
    },
    hasCheckerboarderCreator() {
      const { activeComponent } = this.$store.state.standardNavigation;
      const existRecipe = this.$recipe.checkForCustomCheckerboardComponent(
        activeComponent
      );
      const existTemp = this.$store.state.customCheckerboard.tempComponents[
        this.activeComponent
      ];
      return existRecipe || existTemp;
    },
    configuration() {
      return this.$store.state.utils.configuration;
    },
    recipe_v2() {
      return this.$store.state.recipe.recipe_v2;
    },
    UGCComponent() {
      return this.$store.state.ugc.tempData.features[this.$store.state.ugc.tempData.activeUGCIndex].activeUGCComponent;
    },
    activeComponent() {
      if (this.isApparel) {
        const { activeComponent, activeSubComponent } = this.$store.state.standardNavigation;

        return activeSubComponent ?
          `${ activeComponent }/${ activeSubComponent }` : activeComponent;
      }

      return this.$store.state.standardNavigation.activeComponent;
    },
    componentsWithUGC() {
      return this.$store.getters["utils/getComponentsUgc"]({
        recipe: this.recipe_v2,
      });
    },
    buttonCount() {
      return this.ugcButtonsOrder.length;
    },
    ugcButtonsOrder() {
      // reads the orders of the buttons
      const allButtons = [];
      if (
        this.$store.state.swatches.subgroups &&
        this.$store.state.swatches.subgroups[this.componentSelectedTab]
      ) {
        allButtons.push(
          ...Object.keys(this.$store.state.swatches.subgroups[this.componentSelectedTab])
        );
      }
      // filters only the ones that are enabled
      const enabledButtons = allButtons.filter(
        (featureName) => {
         const result = (this.$store.getters["utils/isUGCenabled"] &&
            featureName === "add-your-image") ||
          (!this.isApparel &&
            this.configuration?.vct_settings?.pattern_maker?.enabled &&
            featureName === "pattern-maker") ||
          (!this.isApparel &&
            this.configuration?.vct_settings?.custom_checkerboard?.enabled &&
            featureName === "custom-checkerboard") ||
          ((this.configuration?.image_library?.models_enabled?.[this.pdm.sku]?.components?.includes(this.activeUGCComponent)) &&
            featureName === "add-your-image-library") ||
          (this.isApparel &&
            this.configuration?.apparel_settings?.pattern_maker?.enabled &&
            featureName === "pattern-maker") ||
          (this.isApparel &&
            this.configuration?.apparel_settings?.custom_checkerboard
              ?.enabled &&
            featureName === "custom-checkerboard");
            return result;
        }
      );

      return enabledButtons;
    },
    isImageLibrary() {
      const activeComponent = this.$store.state.standardNavigation.activeComponent;
      const ugcFeatureType = this.$recipe.getUGCFeatureType(activeComponent);
      const hasLibraryInRecipe = this.$recipe.checkForImageLibrary();
      const isComponentImageLibrary = ugcFeatureType === 'imageLibrary';
      return isComponentImageLibrary && hasLibraryInRecipe;
    },
    pdm() {
      return this.$store.state.utils.pdm;
    },
    componentSelectedTab() {
      return this.$store.state.swatches.selectedTabs[this.activeComponent] || 'add-your-own';
    },
    zoomLevel() {
      return this.$store.state.utils.zoomLevel;
    }
  },
  watch: {
    activeComponent() {
      this.blockTrayButtonsByComponentName();
    },
    ugc_allowed() {
      this.disableEnableUGCButtonsInNav();
    },
    image_library_allowed() {
      this.disableEnableUGCButtonsInNav();
    },
    checkerboard_allowed() {
      this.disableEnableUGCButtonsInNav();
    },
    buttonCount() {
      this.checkEnableAndResizeSwiper();
    },
  },
  methods: {
    launchUploader() {
      this.$refs["hiddenImageUpload"][0].openNativeUploadDialog();
    },
    blockTrayButtonsByComponentName() {
      this.checkPropertyAllowed();
      this.toggleUGCTab();
    },

    checkPropertyAllowed() {
      const { components } = this.recipe_v2;
      const { swatchesRelation } = this.$store.state.swatches;
      let activeCurrentComponent = this.activeComponent;
      let activeSubComponent = null;
      let componentsWithCheckerboardExclusive = [];
      let componentsWithUGCExclusive = [];
      let componentsWithImageLibraryExclusive = [];
      let relation;

      if (this.isApparel) {
        const dataSplit = this.activeComponent.split('/');
        activeCurrentComponent = dataSplit[0];
        activeSubComponent = dataSplit[1] || null;
      }

      for (const component in components) {
        const { sub_components: subComponents, swatch_id: componentSwatchId } =
          components[component];
        relation = swatchesRelation[componentSwatchId];

        if (component !== activeCurrentComponent && relation) {
          if (!relation.checkerboard_allowed) {
            componentsWithCheckerboardExclusive.push(component);
          }

          if (!relation.ugc_allowed) {
            componentsWithUGCExclusive.push(component);
          }

          if (!relation.image_library) {
            componentsWithImageLibraryExclusive.push(component);
          }
        }

        if (subComponents) {
          for (const subComponent in subComponents) {
            relation = swatchesRelation[subComponents[subComponent].swatch_id];

            if ((subComponent !== activeSubComponent ||
              component !== activeCurrentComponent && subComponent === activeSubComponent) &&
              relation) {
              if (!relation.checkerboard_allowed) {
                componentsWithCheckerboardExclusive.push(subComponent);
              }

              if (!relation.ugc_allowed) {
                componentsWithUGCExclusive.push(subComponent);
              }

              if (!relation.image_library) {
                componentsWithImageLibraryExclusive.push(component);
              }
            }
          }
        }
      }

      this.checkerboard_allowed = !componentsWithCheckerboardExclusive.length;
      this.ugc_allowed = !componentsWithUGCExclusive.length;
      this.image_library_allowed = !componentsWithImageLibraryExclusive.length;
    },
    disableEnableUGCButtonsInNav() {
      this.$eventBus.$emit(
        "create-your-own-is-disabled",
        !this.ugc_allowed && !this.checkerboard_allowed && !this.image_library_allowed
      );
    },
    blockButtonsByRecipeID(response) {
      this.checkerboard_allowed = response.checkerboard_allowed;
      this.ugc_allowed = response.ugc_allowed;
      this.image_library_allowed = response.image_library;
      this.$nextTick(() => this.toggleUGCTab());
    },
    toggleUGCTab() {
      this.$eventBus.$emit(
        "disable-ugc-tab",
        !this.ugc_allowed && !this.checkerboard_allowed && !this.image_library_allowed
      );
    },
    setDataLayer() {
      if (!this.$store.getters["utils/useEcomDataLayerFormat"]) {
        this.$ugcDataLayer.logAnalyticsEvent({
          action: "ugc",
          type: this.$ugcDataLayer.types.uploadAPhoto,
        });
      }
    },
    checkEnableAndResizeSwiper() {
      const viewportWidth = window.innerWidth;
      this.enableSwiper = viewportWidth < 640 && this.buttonCount >= 2;
      // if the viewport width has changed since initialization
      if (
        this.enableSwiper &&
        this.$refs.swiper &&
        this.swiperOptions.width !== viewportWidth
      ) {
        // removes the fixed width that was required for the first render
        delete this.$refs.swiper.$swiper.params.width;
        this.$refs.swiper.$swiper.update();
      }
    },
    getComponent(featureName) {
      switch (featureName) {
        case "add-your-image-library":
          return (!this.isImageLibrary) ? "add-your-image-gallery-select" : "add-your-image-gallery-edit";
        case "pattern-maker":
          return !this.hasPattern
            ? "custom-pattern-upload"
            : "custom-pattern-edit";
        case "custom-checkerboard":
          return !this.hasCheckerboarderCreator
            ? "custom-checkerboard-create"
            : "custom-checkerboard-edit";
        case "add-your-image":
          return !this.hasUgc ? "ugc-image-upload" : "ugc-image-edit";
      }
      return "";
    },
    getAttributes(featureName) {
      const { display_name, key } = this.$store.state.swatches.subgroups[this.componentSelectedTab][
        featureName
      ];
      const attributes = {
        textLabel: display_name,
        keyTranslation: key
      };
      switch (featureName) {
        case "add-your-image-library":
          if (!this.image_library_allowed) attributes.class = 'hide';
          attributes.disableButton = !this.image_library_allowed;
          break;
        case "pattern-maker":
          attributes.disableButton = !this.checkerboard_allowed;
          break;
        case "custom-checkerboard":
          attributes.class = "text-center";
          break;
        case "add-your-image":
          attributes.disableButton = !this.ugc_allowed;
          if (!this.hasUgc) {
            attributes.logCustomizerEvents = true;
            attributes.class = "text-center";
          } else if (this.isApparel) {
            attributes.clickOverride = () => this.launchUploader();
          }
          break;
      }
      return attributes;
    },
    getEvents(featureName) {
      const events = {};
      if (featureName === "add-your-image" && !this.hasUgc) {
        events.click = this.setDataLayer;
      }
      return events;
    },
    setSwiperIndex() {
      const featureNames = {
        "add-your-image": "ugc",
        "custom-checkerboard": "customCheckerboard",
        "pattern-maker": "patternMaker",
        "add-your-image-library": "imageLibrary"
      };
      const ugcType = this.$recipe.getUGCFeatureType(this.activeComponent);
      const position = ugcType
        ? this.ugcButtonsOrder.findIndex(
            (elem) => featureNames[elem] == ugcType
          )
        : 0;
      this.$refs.swiper.$swiper.slideTo(position < 0 ? 0 : position);
    },
  },
  created() {
    // sets a fixed width that is required for initializing the swiper when it's hidden
    this.swiperOptions.width = window.innerWidth;
    this.checkEnableAndResizeSwiper();
    if (this.$store.getters["utils/getUrlParameterByName"]("submission_id")) {
      this.$store.commit("ugc/IS_GCC");
    }
    this.$eventBus.$on(
      "block-features-by-swatch-id",
      this.blockTrayButtonsByComponentName
    );
    this.$eventBus.$on(
      "load-relation-swatches",
      this.blockTrayButtonsByComponentName
    );
    this.$eventBus.$on(
      "block-features-by-precreated-recipe",
      this.blockButtonsByRecipeID
    );
    window.addEventListener("resize", this.checkEnableAndResizeSwiper);
  },
  updated() {
    this.enableSwiper && this.setSwiperIndex();
  },
  beforeDestroy() {
    this.$eventBus.$off(
      "block-features-by-swatch-id",
      this.blockTrayButtonsByComponentName
    );
    this.$eventBus.$off(
      "load-relation-swatches",
      this.blockTrayButtonsByComponentName
    );
    this.$eventBus.$off(
      "block-features-by-precreated-recipe",
      this.blockButtonsByRecipeID
    );
    window.removeEventListener("resize", this.checkEnableAndResizeSwiper);
  },
};
</script>
